import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject, input, output } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CalendarEventDetailDialogComponent } from '../calendar-event-detail-dialog/calendar-event-detail-dialog.component';
import CalendarService from 'src/app/services/calendar.service';
import LoadingService from 'src/app/services/loading.service';
import { DefaultSelection36UserFlagPayload, UserFlagService } from 'src/app/api-generated';
import { ISelectItem } from '@aecom/core';
import NavService, { ScreenSize } from '@services/nav.service';
import { cloneDeep } from 'lodash';
import environment from 'src/environments/environment';

interface CalendarViewStorage {
  [view: string]: CalendarDateStorage;
}
interface CalendarDateStorage {
  [date: string]: CalendarStorage;
}
interface CalendarStorage {
  [calendar: string]: ICalendarEvent[];
}

interface BoroughFilter extends ISelectItem {
  keys: string[];
  color: string;
  textColor?: string;
}

interface CalendarSelect extends ISelectItem {
  title: string;
}

export interface ICalendarEvent {
  body: string;
  categories: string[];
  createdDateTime: string;
  localEnd: Date;
  end: string;
  endWithTimeZone: string;
  iCalUId: string;
  id: string;
  importance: string;
  isAllDay: boolean;
  isHtml: boolean;
  isReminderOn: boolean;
  lastModifiedDateTime: string;
  location: string;
  numberOfOccurences: number | null;
  optionalAttendees: string;
  organizer: string;
  recurrence: string;
  recurrenceEnd: string;
  reminderMinutesBeforeStart: number;
  requiredAttendees: string;
  resourceAttendees: string;
  responseRequested: boolean;
  responseTime: string;
  responseType: string;
  selectedDaysOfWeek: number[] | null;
  sensitivity: string;
  seriesMasterId: string | null;
  showAs: string;
  localStart: Date;
  start: string;
  startWithTimeZone: string;
  subject: string;
  timeZone: string;
  webLink: string;
  eventType?: CalendarEventType;
  startMultipleDays?: string; // for multiple days event only
  endMultipleDays?: string; // for multiple days event only
  color?: string;
  textColor?: string;
}

export enum CalendarEventType {
  ALL_DAY = 'all day',
  PARTIAL_SINGLE = 'partial day single',
  PARTIAL_MULTIPLE = 'partial day multiple'
};

export enum CalendarView {
  Month = 'month',
  Week = 'week',
  Day = 'day',
}

export interface ICalendarItem {
  id: string;
  title: string;
  events: ICalendarEvent[];
  colors?: string[];
}

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss']
,
  standalone: false
})
export class CalendarComponent implements OnInit, OnDestroy {
  private loadingService = inject(LoadingService);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private calendarService = inject(CalendarService);
  private userFlagService = inject(UserFlagService);
  private navService = inject(NavService);

  readonly titleChange = output<string>();
  readonly facilityCode = input<string>(undefined); // Set by parent (portal) when navigating from a facility
  
  view: CalendarView = CalendarView.Week;
  eventsStorage: CalendarViewStorage = {
    "week": {},
    "month": {}
  };
  CalendarView = CalendarView;
  weeklyStartDate: Date = new Date();
  weeklyEndDate: Date = new Date();
  calendars: ICalendarItem[] = [];
  selectedMonth: Date = new Date(); // The month currently being viewed
  today = new Date();
  tooltipVisible = false;
  tooltipPosition = { x: 0, y: 0 };
  hoveredEvent: any = null;
  isLoading = true; // Add loading state variable
  usePlan = true;
  private subscriptions: Subscription[] = []; // To manage subscriptions
  private userFlagResolved = false; // prevent initial fetches before flag is loaded
  viewSelect: ISelectItem[] = [
    {
      checked: false,
      id: CalendarView.Week,
      name: "Work Week",
    },
    {
      checked: false,
      id: CalendarView.Month,
      name: "Month",
    }
  ];
  // Single-select: default to 'plan' (PW Planning Calendar only)
  selectedCalendarId: string = 'plan';
  calendarSelect: CalendarSelect[] = [
    {
      checked: false,
      id: 'all',
      name: 'All',
      title: 'All Calendars'
    },
    {
      checked: true,
      id: "plan",
      name: "BBJ Program Calendar",
      title: "BBJ Program Calendar", // - pw-planning@bbjnyc.com
    },
    {
      checked: true,
      id: "program",
      name: "PW Program Calendar",
      title: "PW Program Calendar - pw-calender@bbjnyc.com",
    }
  ];
  boroughFilter: BoroughFilter[] = [
    {
      checked: false,
      id: "bk",
      name: "Brooklyn",
      color: "#ee6000",
      keys:["bk"],
    },
    {
      checked: false,
      id: "bx",
      name: "Bronx",
      color: "#305496",
      keys:["bx"],
    },
    {
      checked: false,
      id: "mn",
      name: "Manhattan",
      color: "#FFC000",
      textColor: "#000000",
      keys:["mn"],
    },
    {
      checked: false,
      id: "qn",
      name: "Queens",
      color: "#00B050",
      keys:["qn"],
    },
  ];
  private selectedBoroughs: BoroughFilter[] = [];

  // Keyword search: when searchKeyword is set, results replace the grid view
  searchInput = '';
  searchKeyword = '';
  searchResults: ICalendarEvent[] = [];
  private searchSubscription?: Subscription;

  // Search covers this many years before and after today
  private readonly searchRangeYears = 2;

  private readonly exportEmailDomains = ['@bbjnyc.com'];

  get canExport(): boolean {
    const email = (this.navService.getUser()?.Email || '').toLowerCase();
    return this.exportEmailDomains.some((domain) => email.endsWith(domain));
  }

  get isSearchMode(): boolean {
    return !!this.searchKeyword;
  }

  // e.g. 'BK-FAC' when viewing the Brooklyn facility calendar; null on Program-wide
  get facilityKeyword(): string | null {
    const code = this.facilityCode() || '';
    const borough = this.boroughFilter.find((b) => code.includes(b.name));
    return borough ? `${borough.id.toUpperCase()}-FAC` : null;
  }

  // Return yyyy-MM-dd string for the date input based on current view
  getDateInputValue(): string {
    const date = this.view === CalendarView.Month ? this.selectedMonth : this.weeklyStartDate;
    // format to yyyy-MM-dd for input[type=date]
    return formatDate(date, 'yyyy-MM-dd', 'en-US');
  }

  onDatePickerChange(value: string) {
    if (!value) return;
    const picked = new Date(value + 'T00:00:00');
    if (this.view === CalendarView.Month) {
      // navigate month to the selected date (set to first of month)
      this.selectedMonth = new Date(picked.getFullYear(), picked.getMonth(), 1);
      this.fetchEvents();
    } else if (this.view === CalendarView.Week) {
      // set weeklyStartDate to Monday of the week containing picked date
      const d = new Date(picked);
      d.setHours(0,0,0,0);
      d.setDate(d.getDate() - d.getDay() + 1);
      this.weeklyStartDate = d;
      this.fetchWeeklyEvents();
    }
  }

  // Model-binding for the aecom-date picker: return a Date object for the picker
  getPickerDate(): Date {
    return this.view === CalendarView.Month ? this.selectedMonth : this.weeklyStartDate;
  }

  // Handler for aecom-date change event. Accepts either a Date or a yyyy-MM-dd string.
  onPickerChange(value: any) {
    if (!value) return;
    let picked: Date;
    if (value instanceof Date) {
      picked = new Date(value.getTime());
    } else if (typeof value === 'string') {
      // try to parse string
      picked = new Date(value);
      if (isNaN(picked.getTime())) {
        // fallback: try appending T00:00:00
        picked = new Date(value + 'T00:00:00');
      }
    } else {
      // unknown type — attempt to coerce
      picked = new Date(value);
    }

    if (isNaN(picked.getTime())) return;

    if (this.view === CalendarView.Month) {
      this.selectedMonth = new Date(picked.getFullYear(), picked.getMonth(), 1);
      this.fetchEvents();
    } else if (this.view === CalendarView.Week) {
      const d = new Date(picked);
      d.setHours(0,0,0,0);
      d.setDate(d.getDate() - d.getDay() + 1);
      this.weeklyStartDate = d;
      this.fetchWeeklyEvents();
    }
  }

  ngOnInit(): void {
    this.loadingService.start();
    if(!environment.production)
    {
      this.exportEmailDomains.push('ryan.li1@aecom.com');
    }

    // Subscribe to selected borough and auto-select matching borough filter
    this.subscriptions.push(
      this.navService.selectedItem$.subscribe((selectedBorough) => {
        if (selectedBorough) {
          const boroughName = selectedBorough.Name || selectedBorough.Code || '';
          // Uncheck all first
          this.boroughFilter.forEach(b => b.checked = false);
          // Check matching borough
          const matchingBorough = this.boroughFilter.find(b => 
            boroughName.toLowerCase().includes(b.name.toLowerCase()) ||
            b.keys.some(key => boroughName.toLowerCase().includes(key.toLowerCase()))
          );
          if (matchingBorough) {
            matchingBorough.checked = true;
            this.changeBoroughFilter([matchingBorough]);
          }
        }
      })
    );

    this.subscriptions.push(
      this.userFlagService.getUserFlag()
        .pipe(
          catchError(() => {
            this.loadingService.stop();
            return of(null);
          })
        )
        .subscribe((flag: DefaultSelection36UserFlagPayload | null) => {
          if (flag) {
            this.view = flag.Calendar as CalendarView;
          }
          // Fetch events based on the determined view
          switch (this.view) {
          case CalendarView.Month:
            this.selectedMonth = new Date();
            this.selectedMonth.setDate(1);
            break;
          case CalendarView.Week:
            this.weeklyStartDate = new Date();
            this.weeklyStartDate.setDate(this.weeklyStartDate.getDate() - this.weeklyStartDate.getDay() + 1);
            break;
          }

          // Debug: view and dates determined by the user flag

          // Mark that we've received the user flag (or default) so initial
          // automatic fetches are allowed after this point. Some UI controls
          // emit change events on init and would otherwise trigger a
          // weekly fetch before the flag is applied; guard against that.
          this.userFlagResolved = true;

          // Fetch events immediately so the calendar is populated on first load
          // when the user flag determines the view. Previously these were
          // commented out which left the calendar empty or populated with stale
          // data from another view.
          if (this.view === CalendarView.Month) {
            this.fetchEvents();
          } else if (this.view === CalendarView.Week) {
            this.fetchWeeklyEvents();
          }

          this.loadingService.stop();
        })
    );
  }

  fetchEvents(): void {
    if (!this.userFlagResolved) {
      return;
    }
    // Search results use a fixed range around today, so navigation doesn't re-query
    if (this.isSearchMode) {
      return;
    }
    let storedEvents = this.getStorageEvents();
    const colors = this.selectedBoroughs.map((b) => b.color);
  let selectedCalendars = this.getSelectedCalendars();
    if(storedEvents)
    {
      this.calendars = selectedCalendars.map((selected)=>{
        return this.genereteCalendarItem(selected, storedEvents, colors);
      });
      this.emitTitle(selectedCalendars);
      return;
    }
    this.isLoading = true; // Set loading to true before starting the fetch

    const { start: formattedStartDate, end: formattedEndDate } = this.getCurrentViewRange();

    // const programEvents$ = this.calendarService.getEvents(formattedStartDate, formattedEndDate); // if we're getting the original program calendar
    const planningEvents$ = this.calendarService.getEvents(formattedStartDate, formattedEndDate, this.usePlan);
    const programEvents$ = planningEvents$; // temp fix
    this.subscriptions.push(
      planningEvents$
        .pipe(
          catchError(() => {
            this.isLoading = false; // Set loading to false if there's an error
            return of(null); // Return null in case of an error
          })
        )
        .subscribe(planData => {
          // Initialize events list
          const planevents = this.prepareMonthlyCalendar(this.getEventList(planData));
          this.setPlanCalendar(planevents);
          storedEvents = this.getStorageEvents();
          this.calendars = selectedCalendars.map((selected)=>{
            return this.genereteCalendarItem(selected, storedEvents, colors);
          });
          this.emitTitle(selectedCalendars);
          this.isLoading = false; // Set loading to false once data is received
        })
    );
  }
  
  prepareMonthlyCalendar(data: ICalendarEvent[]): ICalendarEvent[] {
    // prepareMonthlyCalendar receives raw API items and converts them to local-start/end
    const events: ICalendarEvent[] = [];
    data.forEach((event: ICalendarEvent) => {
      const { localStart, localEnd } = this.getLocalEventRange(event);

      // Determine the event type
      let eventType: CalendarEventType;
      if (event.isAllDay) {
        eventType = CalendarEventType.ALL_DAY;
      } else {
        eventType = localStart.toDateString() === localEnd.toDateString()
          ? CalendarEventType.PARTIAL_SINGLE
          : CalendarEventType.PARTIAL_MULTIPLE;
      }

      // Create base event
      const baseEvent = {
        ...event,
        color: this.getEventColorBasedOnSubject(event.subject),
        textColor: this.getEventTextColorBasedOnSubject(event.subject),
        localStart,
        localEnd,
        start: localStart.toISOString(),
        end: localEnd.toISOString(),
        eventType
      };
      // If the title contains "holiday" treat it as an all-day event so it shows
      // in the all-day area regardless of its original isAllDay flag.
      if (baseEvent.subject && baseEvent.subject.toLowerCase().includes('holiday')) {
        baseEvent.isAllDay = true;
        baseEvent.eventType = CalendarEventType.ALL_DAY;
        const s = new Date(baseEvent.localStart);
        s.setHours(0, 0, 0, 0);
        const e = new Date(baseEvent.localEnd);
        e.setHours(0, 0, 0, 0);
        if (e.getTime() <= s.getTime()) {
          e.setDate(e.getDate() + 1);
        }
        baseEvent.localStart = s;
        baseEvent.localEnd = e;
        baseEvent.start = s.toISOString();
        baseEvent.end = e.toISOString();
      }

      // If the title contains "holiday" treat it as an all-day event so it shows
      // in the all-day area regardless of its original isAllDay flag.
      if (baseEvent.subject && baseEvent.subject.toLowerCase().includes('holiday')) {
        baseEvent.isAllDay = true;
        baseEvent.eventType = CalendarEventType.ALL_DAY;
        // Normalize to midnight-to-midnight for display as all-day
        const s = new Date(baseEvent.localStart);
        s.setHours(0, 0, 0, 0);
        const e = new Date(baseEvent.localEnd);
        e.setHours(0, 0, 0, 0);
        // If end equals start, ensure end is next day to represent a full day
        if (e.getTime() <= s.getTime()) {
          e.setDate(e.getDate() + 1);
        }
        baseEvent.localStart = s;
        baseEvent.localEnd = e;
        baseEvent.start = s.toISOString();
        baseEvent.end = e.toISOString();
      }

      // Create multi-day events (applies to both all-day and partial-day events)
      const rangeStart = baseEvent.localStart;
      const rangeEnd = baseEvent.localEnd;
      if (rangeEnd > rangeStart) {
        let current = new Date(rangeStart);
        
        while (current < rangeEnd) {
          const nextDay = new Date(current);
          nextDay.setDate(current.getDate() + 1);

          events.push({
            ...baseEvent,
            start: new Date(current).toISOString(),
            end: new Date(nextDay).toISOString(),
            startMultipleDays: baseEvent.start,
            endMultipleDays: baseEvent.end
          });
          current = nextDay;
        }
      } else {
        // Add single-day events
        events.push(baseEvent);
      }
    });
    return events;
  }

  
  // Get the first day of the calendar view
  getFirstDayOfCalendar(): Date {
    const year = this.selectedMonth.getFullYear();
    const month = this.selectedMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const startDay = firstDayOfMonth.getDay();
    const prevMonthDays = new Date(year, month, 0).getDate();
    
    // Calculate the start date of the calendar view
    const startDate = new Date(year, month, 1 - startDay);
    return startDate;
  }
  
  // Get the last day of the calendar view
  getLastDayOfCalendar(): Date {
    const year = this.selectedMonth.getFullYear();
    const month = this.selectedMonth.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const endDay = lastDayOfMonth.getDay();
    
    // Calculate the end date of the calendar view
    const endDate = new Date(year, month + 1, 6 - endDay);
    return endDate;
  }

  goToday(): void {
    switch(this.view)
    {
      case CalendarView.Month:
        this.selectedMonth = new Date();
        this.selectedMonth.setDate(1);
        this.fetchEvents();
        break;
      case CalendarView.Week:
        this.weeklyStartDate = new Date();
        this.weeklyStartDate.setDate(this.weeklyStartDate.getDate() - this.weeklyStartDate.getDay() + 1);
        this.fetchWeeklyEvents();
        break;
    }
  }

  goPrev(): void {
    switch(this.view)
    {
      case CalendarView.Month:
        // Create a new date object to avoid mutating the existing one
        this.selectedMonth = new Date(this.selectedMonth.getFullYear(), this.selectedMonth.getMonth() - 1, 1);
        this.fetchEvents(); // Fetch events for the selected month
        break;
      case CalendarView.Week:
        this.weeklyStartDate.setDate(this.weeklyStartDate.getDate() - 7);
        this.weeklyStartDate = new Date(this.weeklyStartDate);
        this.fetchWeeklyEvents();
        break;
    }
  }

  goNext(): void {
    switch(this.view)
    {
      case CalendarView.Month:
        // Create a new date object to avoid mutating the existing one
        this.selectedMonth = new Date(this.selectedMonth.getFullYear(), this.selectedMonth.getMonth() + 1, 1);
        this.fetchEvents(); // Fetch events for the selected month
        break;
      case CalendarView.Week:
        this.weeklyStartDate.setDate(this.weeklyStartDate.getDate() + 7);
        this.weeklyStartDate = new Date(this.weeklyStartDate);
        this.fetchWeeklyEvents();
        break;
    }
    
  }

  changeView(e: ISelectItem): void {
    if (e && !Array.isArray(e)) {
      if(this.view !== e.id)
      {
        this.view = e.id as CalendarView;
        this.subscriptions.push(
          this.userFlagService.setUserFlag({Calendar: this.view}).subscribe()
        );
        if(this.view===CalendarView.Month)
        {
          this.selectedMonth = new Date();
          this.selectedMonth.setDate(1);
          this.fetchEvents();
        } else if(this.view===CalendarView.Week) {
          this.weeklyStartDate = new Date();
          this.weeklyStartDate.setDate(this.weeklyStartDate.getDate() - this.weeklyStartDate.getDay() + 1);
          this.fetchWeeklyEvents();
        }
      }
    } 
  }

  changeCalendar(e: CalendarSelect[]): void {
    // Normalize payload: support both array payloads (old) and single select objects
    const colors = this.boroughFilter.filter((b)=>b.checked).map((b)=>b.color);
    const storedEvents = this.getStorageEvents();
    let selectedId: string = 'all';
    if (Array.isArray(e)) {
      selectedId = e[0] && e[0].id ? e[0].id : 'all';
    } else if (e && (e as any).id) {
      selectedId = (e as any).id;
    }
    this.selectedCalendarId = selectedId || 'all';

    const selectedCalendars = this.getSelectedCalendars();

    this.navService.screenSize = selectedCalendars.length > 1 ? ScreenSize.Full : ScreenSize.Part;
    this.calendars = selectedCalendars.map((selected) => this.genereteCalendarItem(selected, storedEvents, colors));
    this.emitTitle(selectedCalendars);
  }

  changeBoroughFilter(e: BoroughFilter[]): void {
    this.selectedBoroughs = e;
    
    let colors: string[] = [];
    const storedEvents = this.getStorageEvents();
    let selectedCalendars = this.getSelectedCalendars();
    if (!selectedCalendars || selectedCalendars.length === 0) {
      // This logic is in changeCalendar, but good to have here for safety
      // if borough filter is changed with no calendars selected.
      selectedCalendars = this.calendarSelect;
      this.selectedCalendarId = 'all';
    }
    if(e&&e.length>0)
    {
      colors = e.map((b)=>b.color);
    }
    
    this.calendars = selectedCalendars.map((selected)=>{
      return this.genereteCalendarItem(selected, storedEvents, colors);
    });
  }

  viewDay(): void {
    this.view = CalendarView.Day;
  }

  fetchWeeklyEvents(): void {
    if (!this.userFlagResolved) {
      return;
    }
    // Ensure weeklyEndDate is always computed from the current weeklyStartDate
    // even when we return early due to cached storedEvents. This prevents
    // the header range from becoming stale when using cached data.
  this.weeklyEndDate = new Date(this.weeklyStartDate);
  // Set to Friday (4 days after Monday)
  this.weeklyEndDate.setDate(this.weeklyStartDate.getDate() + 4);

    // Search results use a fixed range around today, so navigation doesn't re-query
    if (this.isSearchMode) {
      return;
    }

    let storedEvents = this.getStorageEvents();
    const colors = this.selectedBoroughs.map((b) => b.color);
  let selectedCalendars = this.getSelectedCalendars();
    if(storedEvents)
    {
      this.calendars = selectedCalendars.map((selected)=>{
        return this.genereteCalendarItem(selected, storedEvents, colors);
      });
      this.emitTitle(selectedCalendars);
      return;
    }
  this.isLoading = true; // Set loading to true before starting the fetch
  const { start: formattedStartDate, end: formattedEndDate } = this.getCurrentViewRange();

    // const programEvents$ = this.calendarService.getEvents(formattedStartDate, formattedEndDate);
    const planningEvents$ = this.calendarService.getEvents(formattedStartDate, formattedEndDate, this.usePlan);
    const programEvents$ = planningEvents$;
    this.subscriptions.push(
      planningEvents$
        .pipe(
          catchError(() => {
            this.isLoading = false; // Set loading to false if there's an error
            return of(null); // Return null in case of an error
          })
        )
        .subscribe(planData => {
          // Initialize events list
          const planevents = this.prepareWeeklyCalendar(this.getEventList(planData));
          this.setPlanCalendar(planevents);
          storedEvents = this.getStorageEvents();
          this.calendars = selectedCalendars.map((selected)=>{
            return this.genereteCalendarItem(selected, storedEvents, colors);
          });
          this.emitTitle(selectedCalendars);
          this.isLoading = false; // Set loading to false once data is received
        })
    );
  }

  prepareWeeklyCalendar(data: ICalendarEvent[]): ICalendarEvent[] {
    const events: ICalendarEvent[] = [];

    data.forEach((event: ICalendarEvent) => {
      const { localStart, localEnd } = this.getLocalEventRange(event);

      // Determine the event type
      let eventType: CalendarEventType;
      if (event.isAllDay) {
        eventType = CalendarEventType.ALL_DAY;
      } else {
        eventType = localStart.toDateString() === localEnd.toDateString()
          ? CalendarEventType.PARTIAL_SINGLE
          : CalendarEventType.PARTIAL_MULTIPLE;
      }

      // Create base event
      const baseEvent = {
        ...event,
        color: this.getEventColorBasedOnSubject(event.subject),
        textColor: this.getEventTextColorBasedOnSubject(event.subject),
        localStart,
        localEnd,
        start: localStart.toISOString(),
        end: localEnd.toISOString(),
        eventType
      };
      events.push(baseEvent);
    });
    return events;
  }

  private getLocalEventRange(event: ICalendarEvent): { localStart: Date; localEnd: Date } {
    if (!event.isAllDay) {
      return {
        localStart: new Date(`${event.startWithTimeZone}`),
        localEnd: new Date(`${event.endWithTimeZone}`)
      };
    }

    // All-day values represent calendar dates; parse date-only to avoid timezone shifts.
    const localStart = this.parseDateOnlyAsLocalMidnight(event.startWithTimeZone);
    const localEnd = this.parseDateOnlyAsLocalMidnight(event.endWithTimeZone);

    // Keep end exclusive for all-day ranges; ensure minimum one-day span.
    if (localEnd.getTime() <= localStart.getTime()) {
      localEnd.setDate(localEnd.getDate() + 1);
    }

    return { localStart, localEnd };
  }

  private parseDateOnlyAsLocalMidnight(value: string): Date {
    const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})/.exec(value || '');
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]) - 1;
      const day = Number(match[3]);
      return new Date(year, month, day);
    }

    const parsed = new Date(value);
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }

  // API date range (yyyy-MM-dd) for the current view
  private getCurrentViewRange(): { start: string; end: string } {
    let startDate: Date;
    let endDate: Date;
    if (this.view === CalendarView.Month) {
      // Whole calendar grid plus one buffer day on each side
      startDate = this.getFirstDayOfCalendar();
      startDate.setDate(startDate.getDate() - 1);
      endDate = this.getLastDayOfCalendar();
      endDate.setDate(endDate.getDate() + 1);
    } else {
      // The API may treat the end date as exclusive (or interpret midnight as the next day),
      // so request up through the day after the visible end (Saturday) to ensure all
      // Friday events are returned.
      startDate = new Date(this.weeklyStartDate);
      endDate = new Date(this.weeklyEndDate);
      endDate.setDate(endDate.getDate() + 1);
    }
    return {
      start: formatDate(startDate, 'yyyy-MM-dd', 'en-US'),
      end: formatDate(endDate, 'yyyy-MM-dd', 'en-US'),
    };
  }

  applySearch(): void {
    const keyword = (this.searchInput || '').trim();
    if (!keyword) {
      this.clearSearch();
      return;
    }
    this.searchKeyword = keyword;
    this.fetchSearchResults();
  }

  clearSearch(): void {
    const wasSearching = this.isSearchMode;
    this.searchSubscription?.unsubscribe();
    this.searchInput = '';
    this.searchKeyword = '';
    this.searchResults = [];
    if (wasSearching) {
      // Back to the standard calendar view (keyword 'none')
      if (this.view === CalendarView.Month) {
        this.fetchEvents();
      } else {
        this.fetchWeeklyEvents();
      }
    }
    this.cdr.detectChanges();
  }

  // Search results are not cached in eventsStorage since that cache is not keyword-aware
  fetchSearchResults(): void {
    const today = new Date();
    const startDate = new Date(today.getFullYear() - this.searchRangeYears, today.getMonth(), today.getDate());
    const endDate = new Date(today.getFullYear() + this.searchRangeYears, today.getMonth(), today.getDate() + 1);
    const start = formatDate(startDate, 'yyyy-MM-dd', 'en-US');
    const end = formatDate(endDate, 'yyyy-MM-dd', 'en-US');
    this.searchSubscription?.unsubscribe();
    this.isLoading = true;
    this.cdr.detectChanges();
    this.searchSubscription = this.calendarService
      .getEvents(start, end, this.usePlan, this.searchKeyword)
      .pipe(catchError(() => of(null)))
      .subscribe((data) => {
        this.searchResults = this.toListEvents(this.getEventList(data));
        this.isLoading = false;
        this.cdr.detectChanges();
      });
  }

  // Search results with the borough filter applied, as shown in the list
  get visibleSearchResults(): ICalendarEvent[] {
    return this.applyBoroughFilter(this.searchResults);
  }

  // Exports exactly what's on screen: the search list, or the current week/month with filters applied
  exportCsv(): void {
    if (!this.canExport || this.isLoading) {
      return;
    }
    // Recurring events come back expanded, so each occurrence is its own row
    const events = this.isSearchMode ? this.visibleSearchResults : this.getVisibleGridEvents();
    const rows = [
      ['Subject', 'Start', 'End'],
      ...events.map((e) => [e.subject, this.formatListStart(e), this.formatListEnd(e)]),
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const prefix = this.facilityKeyword ? `${this.facilityKeyword} ` : '';
    const fileName = `${prefix}PW-Calendar_${formatDate(new Date(), 'yyyyMMdd', 'en-US')}.csv`;
    this.downloadFile(`﻿${csv}`, fileName, 'text/csv;charset=utf-8;');
  }

  // Same rule as the week/month views: holidays always pass, otherwise match selected borough colors
  private applyBoroughFilter(events: ICalendarEvent[]): ICalendarEvent[] {
    const colors = this.selectedBoroughs.map((b) => b.color);
    if (!colors.length) {
      return events;
    }
    return events.filter((event) =>
      (event.subject && event.subject.toLowerCase().includes('holiday')) || colors.includes(event.color ?? '')
    );
  }

  // Events visible in the current week (Mon-Fri) or month grid, de-duplicated and sorted
  private getVisibleGridEvents(): ICalendarEvent[] {
    let rangeStart: Date;
    let rangeEnd: Date;
    if (this.view === CalendarView.Month) {
      rangeStart = this.getFirstDayOfCalendar();
      rangeEnd = this.getLastDayOfCalendar();
    } else {
      rangeStart = new Date(this.weeklyStartDate);
      rangeEnd = new Date(this.weeklyEndDate);
    }
    rangeStart.setHours(0, 0, 0, 0);
    rangeEnd.setHours(0, 0, 0, 0);
    rangeEnd.setDate(rangeEnd.getDate() + 1); // exclusive

    // Month view splits multi-day events into one entry per day; keep one row per event
    const seen = new Set<string>();
    const events: ICalendarEvent[] = [];
    this.calendars.forEach((calendar) => {
      this.applyBoroughFilter(calendar.events).forEach((event) => {
        const key = `${event.id}|${new Date(event.localStart).getTime()}`;
        const overlaps = new Date(event.localStart) < rangeEnd && new Date(event.localEnd) > rangeStart;
        if (overlaps && !seen.has(key)) {
          seen.add(key);
          events.push(event);
        }
      });
    });
    return events.sort((a, b) => new Date(a.localStart).getTime() - new Date(b.localStart).getTime());
  }

  formatListStart(event: ICalendarEvent): string {
    return formatDate(event.localStart, event.isAllDay ? 'MM/dd/yyyy' : 'MM/dd/yyyy hh:mm a', 'en-US');
  }

  formatListEnd(event: ICalendarEvent): string {
    if (!event.isAllDay) {
      return formatDate(event.localEnd, 'MM/dd/yyyy hh:mm a', 'en-US');
    }
    // All-day end is exclusive; show the last day of the event
    const lastDay = new Date(event.localEnd);
    lastDay.setDate(lastDay.getDate() - 1);
    return formatDate(lastDay, 'MM/dd/yyyy', 'en-US');
  }

  // API returns { value: [...] }; accept a bare array too in case the Logic App output isn't wrapped
  private getEventList(data: any): ICalendarEvent[] {
    if (Array.isArray(data)) {
      return data;
    }
    return Array.isArray(data?.value) ? data.value : [];
  }

  private toListEvents(data: ICalendarEvent[] | undefined): ICalendarEvent[] {
    return (data || [])
      .map((event) => {
        const { localStart, localEnd } = this.getLocalEventRange(event);
        return { ...event, localStart, localEnd, color: this.getEventColorBasedOnSubject(event.subject) };
      })
      .sort((a, b) => a.localStart.getTime() - b.localStart.getTime());
  }

  private downloadFile(content: string, fileName: string, type: string): void {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  showEventDetails(event: ICalendarEvent): void {
    this.dialog.open(CalendarEventDetailDialogComponent, {
      data: event
    });
  }

  showTooltip(event: any, mouseEvent: MouseEvent): void {
    this.tooltipVisible = true;
    this.hoveredEvent = event;
    this.tooltipPosition.x = mouseEvent.pageX + 5;
    this.tooltipPosition.y = mouseEvent.pageY + 10;
  }

  hideTooltip() {
    this.tooltipVisible = false;
    this.hoveredEvent = null;
    this.cdr.detectChanges(); // Ensure changes are detected
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions to prevent memory leaks
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.searchSubscription?.unsubscribe();
  }

  fetchCalendarUsers() {
    this.subscriptions.push(
      this.calendarService.getCalendarUsers()
        .pipe(
          catchError(() => {
            this.isLoading = false; // Set loading to false if there's an error
            return of([]); // Return an empty array in case of an error
          })
        )
        .subscribe(data => {
          //console.log('users:', data)
        })
    );
  }
  
  getEventColorBasedOnSubject(subject: string): string | undefined {
    if(!subject) return undefined;
    for (const borough of this.boroughFilter) {
      const hasKey = borough.keys.some((key) =>
        subject.toLowerCase().includes(key.toLowerCase())
      );
      if (hasKey) {
        return borough.color;
      }
    }
    return undefined;
  }

  // Return a borough-specific text color for a subject if available
  getEventTextColorBasedOnSubject(subject: string): string | undefined {
    if (!subject) return undefined;
    for (const borough of this.boroughFilter) {
      const hasKey = borough.keys.some((key) =>
        subject.toLowerCase().includes(key.toLowerCase())
      );
      if (hasKey) {
        return borough.textColor;
      }
    }
    return undefined;
  }
  
  getStorageEvents(): CalendarStorage | undefined {
    const date = this.view === CalendarView.Month ? this.selectedMonth : this.weeklyStartDate;
    const dateString = formatDate(date, 'MM-dd-yyyy', 'en-US');
    return this.eventsStorage[this.view][dateString];
  }
  getStoredCalendarEvents(storage: CalendarStorage | undefined, id: string): ICalendarEvent[] {
    if(!storage) return [];
    return storage[id] || [];
  }

  genereteCalendarItem(selected: CalendarSelect, storedEvents: CalendarStorage | undefined, colors: string[]): ICalendarItem {
    const events = this.getStoredCalendarEvents(storedEvents, selected.id);
    return {
      id: selected.id,
      title: selected.title,
      events: cloneDeep(events),
      colors: colors
    };
  }

  // Return the CalendarSelect items corresponding to the current single selection
  getSelectedCalendars(): CalendarSelect[] {
    if (this.selectedCalendarId === 'all') {
      return this.calendarSelect.filter(c => c.id !== 'all');
    }
    return this.calendarSelect.filter(c => c.id === this.selectedCalendarId);
  }

  setProgramCalendar(events: ICalendarEvent[]): void {
    const date = this.view === CalendarView.Month ? this.selectedMonth : this.weeklyStartDate;
    const dateString = formatDate(date, 'MM-dd-yyyy', 'en-US');
    // Ensure the container objects exist before spreading
    if (!this.eventsStorage[this.view]) {
      this.eventsStorage[this.view] = {} as CalendarDateStorage;
    }
    const existing = this.eventsStorage[this.view][dateString] || {};
    this.eventsStorage[this.view][dateString] = {
      ...existing,
      "program": events
    };
  }

  setPlanCalendar(events: ICalendarEvent[]): void {
    const date = this.view === CalendarView.Month ? this.selectedMonth : this.weeklyStartDate;
    const dateString = formatDate(date, 'MM-dd-yyyy', 'en-US');
    // Ensure the container objects exist before spreading
    if (!this.eventsStorage[this.view]) {
      this.eventsStorage[this.view] = {} as CalendarDateStorage;
    }
    const existing = this.eventsStorage[this.view][dateString] || {};
    this.eventsStorage[this.view][dateString] = {
      ...existing,
      "plan": events
    };
  }

  private emitTitle(selectedCalendars: CalendarSelect[]): void {
    if (selectedCalendars.length > 1) {
      this.titleChange.emit('BBJ Program');
    } else if (selectedCalendars.length === 1) {
      this.titleChange.emit(selectedCalendars[0].name);
    } else {
      this.titleChange.emit('Program-wide Calendar'); // Fallback title
    }
  }
}
